import { useEffect } from 'react'
import { preferencesStore } from '../services/preferencesStore'
import type { ThemePreference } from '../types'

/**
 * Theme switcher (Track D)。
 *
 * 動作:
 *   - preferencesStore.subscribe で `theme` の変更を検出
 *   - 'system': `prefers-color-scheme: dark` を読み、light/dark に解決
 *   - 'light' / 'dark': そのまま `document.documentElement.dataset.theme` に書く
 *
 * CSS 側は `:root[data-theme='dark']` セレクタで token を上書きする (theme.css)。
 * 単一の hook を App.tsx ルート級で 1 回だけ呼ぶ前提 (subscribe / matchMedia
 * listener が重複登録されないよう unmount cleanup を厳守)。
 */

const resolveSystemPreference = (): 'light' | 'dark' => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const applyTheme = (theme: ThemePreference): void => {
  if (typeof document === 'undefined') return
  const resolved = theme === 'system' ? resolveSystemPreference() : theme

  document.documentElement.dataset.theme = resolved
}

export const useTheme = (): void => {
  useEffect(() => {
    // 初期値を即適用 (cache hit 前でも defaults で system → 解決)
    applyTheme(preferencesStore.getResolved().theme)

    const unsubscribe = preferencesStore.subscribe(prefs => {
      applyTheme(prefs.theme)
    })

    // 'system' 選択時に OS 側の light/dark 切替を listen
    let cleanupMql: (() => void) | null = null
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)')

      const onChange = (): void => {
        if (preferencesStore.getResolved().theme === 'system') {
          applyTheme('system')
        }
      }

      mql.addEventListener('change', onChange)
      cleanupMql = () => mql.removeEventListener('change', onChange)
    }

    return (): void => {
      unsubscribe()
      cleanupMql?.()
    }
  }, [])
}
